// working code for all pages

import { apiAboutPage } from "../../services/api/Pages";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "../Header";

/* ================= TYPES ================= */
interface PageData {
  id: number;
  name: string;
  positions?: string[];
  extra_positions?: string[];
  title: string;
  slug: string;
  body: string;
  image?: string;
  background_image?: string;
}

/* ================= COMPONENT ================= */
const About = () => {
  const { slug } = useParams<{ slug: string }>();

  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await apiAboutPage(`pages/${slug || "about-hoa"}`);
        const result = res;
        if (result && result.data.data) {
          setPage(result.data.data);
        } else {
          setError("Page not found");
        }
      } catch (err) {
        setError("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  /* ================= STATES ================= */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xl font-semibold">
        Loading...
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error || "No content available"}
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="w-full pb-16">
      <PageHeader title={page.title} slug={slug} />
      <div className="w-full  px-4 pb-8 sm:px-6 sm:pb-10 md:px-8 md:pb-12 lg:px-10 lg:pb-16">
        {/* IMAGE SECTION ABOVE */}
        {page.image && page.image !== "null" && page.image.trim() !== "" && (
          <div className="w-full flex justify-center mt-8">
            <div className="w-full md:w-[500px] overflow-hidden rounded-xl shadow-lg border border-gray-200 bg-white">
              {/* IMAGE */}
              <div className="w-full">
                <img
                  src={page.image}
                  alt={page.title}
                  className="w-full h-[400px] object-contain"
                />
              </div>

              {/* DETAILS */}
              <div className="p-6 text-center">
                <h2 className="text-2xl font-bold text-blue-700 mb-3">
                  {page.name}
                </h2>

                {/* POSITIONS */}
                <div className="flex flex-col items-center">
                  {Array.isArray(page.positions) &&
                    page.positions.length > 0 &&
                    page.positions.map((pos, idx) => (
                      <p
                        key={idx}
                        className="text-xl italic text-black leading-relaxed"
                      >
                        {pos}
                        {idx < (page.positions?.length || 0) - 1 ? "," : "."}
                      </p>
                    ))}
                </div>

                {/* EXTRA POSITIONS */}
                <div className="flex flex-col items-center mt-2">
                  {Array.isArray(page.extra_positions) &&
                    page.extra_positions.length > 0 &&
                    page.extra_positions.map((pos, idx) => (
                      <p
                        key={idx}
                        className="text-lg italic text-gray-700 leading-relaxed"
                      >
                        {pos}
                        {idx < (page.extra_positions?.length || 0) - 1
                          ? ","
                          : "."}
                      </p>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* MAIN CONTAINER */}
        <div className="relative max-w-7xl mx-auto rounded-2xl overflow-hidden mt-8">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${page.background_image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-b from-sky-100/90 to-sky-200/90" />

          <div className="relative z-10 p-6 md:p-10">
            {/* BODY CONTENT */}
            <div>
              <section
                className="
          prose prose-lg max-w-none
          prose-headings:text-black
          prose-p:text-gray-700
          prose-li:text-gray-700
          prose-ul:pl-5
          leading-8
          bg-gradient-to-b from-sky-100 to-sky-200
        "
                dangerouslySetInnerHTML={{ __html: page.body || "" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
